import dayjs from 'dayjs';
import {
  DefaultFetchError,
  FetchCreate,
  FetchDelete,
  FetchGet,
  FetchGetId,
  FetchSuccess,
  FetchUpdate,
  PagingDataResponse,
  PagingParams,
  useFetchCreate,
  useFetchDelete,
  useFetchGet,
  useFetchGetId,
  useFetchUpdate,
} from '../fetch';
import { IContract } from './contracts';
import { User, UserStatusType } from '../../store/auth';
import {
  IAddress,
  IBankAccount,
  IPhoneNumber,
  LegalForm,
  SalutationType,
  Title,
} from '../../types/businessPartnerRelated';
import { createSearchParamsString } from '../../utils';
import { MetaDataInfo } from '../../store/types';

export interface BusinessInternalAPIParams {
  id: string;
  status: UserStatusType;
  user: User;
  newEmail?: string;
  createdAt: string;
  updatedAt: string;
  synchronizedAt: string | null;
}

export interface BusinessExtraParams {
  phoneNumbers: IPhoneNumber[];
  salutation: number | string | null;
  title: number | string | null;
  bankAccounts: IBankAccount[] | null;
  address: IAddress | null;
}

export interface ISearchBusinessPartnerExtended
  extends BusinessInternalAPIParams,
    BusinessExtraParams {
  businessPartnerId: number;
  contractId: number;
  contractNumber: string;
  contractSection: string;
  businessPartnerNumber: string;
  businessPartnerSection: unknown;
  firstName: string;
  name: string;
  legalForm: number; // string; "1" for example
  dateOfBirth: number;
  email: string;
  phoneNumber: string;
  street: string;
  housenumber: string;
  housenumberAddition: string;
  postalCode: string;
  city: string;
  addressAddition: unknown;
  meteringPoint: string;
  deviceNumber: string;
  roleId: unknown;
  bankAccountNumber: unknown;
  bankAccountIban: string;
  ilnCode: unknown;
  roleName: string;
}

export interface BusinessPartnerWithContracts
  extends ISearchBusinessPartnerExtended {
  contracts: IContract[];
}

export enum BusinessStatusDisplay {
  'active' = 'Active',
  'deactivated' = 'Deactivated',
  'pending' = 'Pending',
  'archived' = 'Archived',
}

export interface IGetUserBusinessPartnersParams extends PagingParams {
  userId?: string;
  businessPartnerNumber?: string;
  orderByColumn?: 'id' | 'name' | 'createdAt' | 'updatedAt';
}

export const useBusinessPartnersGet = <DD = BusinessPartnersTableData>(
  decorateData?: (data: PagingDataResponse<BusinessPartnerWithContracts>) => DD,
): FetchGet<
  PagingDataResponse<BusinessPartnerWithContracts>,
  IGetUserBusinessPartnersParams,
  DefaultFetchError,
  DD
> => useFetchGet('businesses', {
    autoStart: false,
    startStateLoading: false,
    decorateData,
  });

export interface BusinessPartnerTableRow
  extends Omit<BusinessPartnerWithContracts, 'settings'> {
  key: string;
  fullName: string;
}

export interface BusinessPartnersTableData {
  data: BusinessPartnerTableRow[];
  meta: MetaDataInfo;
  total: number;
}

export const useBusinessPartnersTableGet = () => useBusinessPartnersGet<BusinessPartnersTableData>(
  ({
    data,
    meta,
  }: PagingDataResponse<BusinessPartnerWithContracts>): BusinessPartnersTableData => ({
    data: data.map(
      ({
        ...item
      }: BusinessPartnerWithContracts): BusinessPartnerTableRow => ({
        key: item.id,
        fullName: `${item.firstName || '-'} ${item.name || '-'}`,
        ...item,
      }),
    ),
    total: meta.itemCount,
    meta,
  }),
);

export const useBusinessPartnerGetById = <DD = BusinessPartnerWithContracts>(
  id?: string,
  decorateData?: (data: BusinessPartnerWithContracts) => DD,
): FetchGetId<BusinessPartnerWithContracts, DefaultFetchError, unknown, DD> => useFetchGetId('businesses', id, {
    autoStart: !!id,
    decorateData,
  });

/** Update flow */
export interface IUpdateBusinessPartnerParams {
  businessPartnerNumber: string;
  newLegalForm: LegalForm;
  newSalutation: SalutationType;
  newTitle: Title;
  newFirstname: string;
  newLastname: string;
  newDateOfBirth: dayjs.Dayjs | null; // send as string;
}

export interface IUpdateMailAddressParams {
  businessPartnerNumber: string;
  newEmailAddress: string;
  communicationConsentOptInDate?: string;
  communicationConsentOptInOriginalWording?: string;
}

export interface IUpdateOrCreatePhoneNumberParams {
  advertisementConsentROList: [];
  businessPartner: number;
  creationDate: string;
  number: string;
  id?: number;
}

export interface IUpdateAddressIfChangedParams {
  businessPartnerNumber: string;
  adressPortal: {
    changedCreateE03: boolean;
    city: string;
    housenumber: string;
    housenumberAddition?: string;
    id?: number;
    postalCode: string;
    street: string;
  };
}

export interface UpdateBankAccountParams {
  accountOwnerName: string;
  contractNumber: string;
  customerInformed: boolean;
  iban: string;
  periodStart: string; // number
  bankName?: string; // Field exist in form, but not editable
}

export interface BusinessPartnersCreateParams {
  personalData: {
    businessPartner: Partial<IUpdateBusinessPartnerParams>;
    emailAddress: Partial<IUpdateMailAddressParams>;
    phoneNumber: Partial<IUpdateOrCreatePhoneNumberParams>;
  };
  billingAddress: Partial<IUpdateAddressIfChangedParams>;
  // paymentMethod: Partial<UpdateBankAccountParams>,
}

export interface ContractCreateParams {
  paymentMethod: UpdateBankAccountParams; // Partial<>
}

// export const useBusinessCreate = (): FetchCreate<FetchSuccess, DefaultFetchError, BusinessPartnersCreateParams> => (
//   useFetchCreate('businesses')
// );

export type BusinessUpdateParams = Partial<BusinessPartnersCreateParams>;

// export const useBusinessUpdate = (id?: string): FetchUpdate<
//   BusinessPartnerWithContracts, DefaultFetchError, BusinessUpdateParams> => ( // FetchSuccess
//   useFetchUpdate('businesses', id)
// );

export const useBusinessDelete = (
  id?: string,
): FetchDelete<FetchSuccess, DefaultFetchError> => useFetchDelete('businesses', id);

export const useBusinessSyncWithMBS = (
  businessPartnerNumber?: string,
): FetchUpdate<FetchSuccess, DefaultFetchError, undefined> => ( // FetchSuccess
  useFetchUpdate(
    'businesses',
    businessPartnerNumber ? `${businessPartnerNumber}/parse` : '',
  )
);

// in this request need to send values as query params using 'createSearchParamsString({...})'
export const useUpdateBusinessPartner = (): FetchCreate<
  unknown,
  DefaultFetchError,
  IUpdateBusinessPartnerParams
> => useFetchCreate('users/update-business-partner');

export const useUpdateEmailAddress = (): FetchUpdate<
  unknown,
  DefaultFetchError,
  IUpdateMailAddressParams
> => useFetchUpdate('businesses/change-email');
// useFetchCreate('users/update-mail-address')

export const useUpdatePhoneNumber = (): FetchCreate<
  unknown,
  DefaultFetchError,
  IUpdateOrCreatePhoneNumberParams
> => useFetchCreate('users/update-or-create-phone-number');

export const useUpdateBillingAddress = (): FetchCreate<
  unknown,
  DefaultFetchError,
  IUpdateAddressIfChangedParams['adressPortal']
> => useFetchCreate('users/update-address-if-changed'); // businessPartnerNumber as query param

/** Load extended business partner info */
// `/proxy/portal/masterdata/loadBusinessPartner?businessPartnerNumber=${businessPartnerNumber.replace("-1", "")}`

export const useLoadBusinessPartner = (
  businessPartnerNumber?: string,
): FetchGetId<
  BusinessPartnerWithContracts,
  DefaultFetchError,
  unknown,
  BusinessPartnerWithContracts
> => useFetchGetId(
  'proxy/portal/masterdata/loadBusinessPartner',
  `${createSearchParamsString({ businessPartnerNumber })}`,
  { autoStart: !!businessPartnerNumber },
);
